import type { MascotGameState } from './mascot-game-types'
import { gameDay, projectNeeds } from './mascot-game-view'

export const MASCOT_CARE_OPTIONS = {
  energy: {
    title: 'Pílula de energia',
    button: 'Comprar pílula',
    result: 'A energia do mascote foi preenchida até 100%.',
    description:
      'a energia do seu mascote será preenchida até 100% após a confirmação do Pix.',
    free: 'O sono continua disponível gratuitamente.',
  },
  satiety: {
    title: 'Saciedade completa',
    button: 'Comprar saciedade',
    result: 'A saciedade do mascote foi preenchida até 100%.',
    description:
      'a saciedade do seu mascote será preenchida até 100% após a confirmação do Pix.',
    free: 'Você também pode servir os alimentos da despensa ou usar o lanche gratuito quando disponível.',
  },
  wait_daily: {
    title: 'Liberar outro presente',
    button: 'Liberar presente',
    result: 'Você pode abrir outro presente.',
    description: 'você recebe uma liberação para abrir outro presente sem esperar.',
    free: 'O próximo presente diário continua gratuito.',
  },
  wait_game_start: {
    title: 'Jogar sem esperar',
    button: 'Liberar jogo',
    result: 'Uma partida pode começar sem esperar o intervalo.',
    description:
      'você recebe uma liberação para iniciar uma partida sem esperar o intervalo. Energia e saciedade continuam necessárias.',
    free: 'Você também pode aguardar o intervalo gratuitamente.',
  },
  wait_feed: {
    title: 'Liberar refeição',
    button: 'Liberar refeição',
    result: 'Uma refeição foi liberada sem espera.',
    description:
      'você recebe uma liberação para servir uma refeição sem esperar o intervalo. O alimento é consumido da despensa.',
    free: 'Você também pode aguardar o intervalo gratuitamente.',
  },
  wait_water: {
    title: 'Liberar ducha',
    button: 'Liberar ducha',
    result: 'Uma ducha foi liberada sem espera.',
    description: 'você recebe uma liberação para dar uma ducha sem esperar o intervalo.',
    free: 'Você também pode aguardar a ducha gratuita.',
  },
  wait_wash: {
    title: 'Liberar banho',
    button: 'Liberar banho',
    result: 'Um banho foi liberado sem espera.',
    description:
      'você recebe uma liberação para dar um banho sem esperar o intervalo. O sabonete é consumido da despensa.',
    free: 'Você também pode aguardar o intervalo gratuitamente.',
  },
  wait_heal: {
    title: 'Liberar suco de energia',
    button: 'Liberar suco',
    result: 'Um suco de energia foi liberado sem espera.',
    description:
      'você recebe uma liberação para usar um suco sem esperar o intervalo. O suco é consumido da despensa.',
    free: 'Você também pode aguardar o intervalo gratuitamente.',
  },
  wait_snack: {
    title: 'Liberar lanchinho',
    button: 'Liberar lanchinho',
    result: 'Um lanchinho foi liberado sem espera.',
    description:
      'você recebe uma liberação do intervalo do lanchinho. A saciedade precisa estar abaixo de 45.',
    free: 'O lanche continua gratuito quando o intervalo terminar.',
  },
  wait_harvest: {
    title: 'Antecipar colheita',
    button: 'Liberar colheita',
    result: 'Uma colheita antecipada foi liberada.',
    description: 'você recebe uma liberação para colher sem esperar o crescimento.',
    free: 'Você também pode aguardar o crescimento gratuitamente.',
  },
  wait_pet: {
    title: 'Liberar recompensa de carinho',
    button: 'Liberar recompensa',
    result: 'Uma recompensa de carinho foi liberada sem espera.',
    description:
      'você recebe uma liberação do intervalo da recompensa de carinho. Os limites diários de XP continuam valendo.',
    free: 'Fazer carinho continua gratuito e à vontade.',
  },
} as const

export type MascotCareBenefit = keyof typeof MASCOT_CARE_OPTIONS
export type MascotWaitAction =
  | 'daily'
  | 'game_start'
  | 'feed'
  | 'water'
  | 'wash'
  | 'heal'
  | 'snack'
  | 'harvest'
  | 'pet'

export function hasWaitPass(
  state: { wait_passes?: Partial<Record<MascotWaitAction, number>> },
  action: MascotWaitAction
) {
  return (state.wait_passes?.[action] ?? 0) > 0
}

/** A completed need or an already available activity cannot be bought again. */
export function mascotCareValue(
  benefit: MascotCareBenefit,
  state: MascotGameState,
  now: number
): number {
  if (benefit === 'energy' || benefit === 'satiety')
    return projectNeeds(state, now)[benefit]
  const action = benefit.slice(5) as MascotWaitAction
  if (hasWaitPass(state, action)) return 100
  const waiting =
    action === 'daily'
      ? state.last_gift === gameDay(now)
      : action === 'harvest'
        ? (state.garden?.ready_at ?? 0) > now
        : (state.cooldowns[action] ?? 0) > now
  return waiting ? 0 : 100
}
